import React, { useCallback, useEffect, useRef, useState } from 'react';

const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value)));

export function MixRange({ testId, label, value, min = 0, max = 100, step = 1, vertical = false, onDraft, onCommit, className = '' }) {
  const [draft, setDraft] = useState(value);
  const draftRef = useRef(value);
  const pointerRef = useRef(false);
  const keyboardTimer = useRef(null);
  useEffect(() => { if (!pointerRef.current) { draftRef.current = value; setDraft(value); } }, [value]);
  useEffect(() => () => clearTimeout(keyboardTimer.current), []);
  const commit = useCallback(() => {
    clearTimeout(keyboardTimer.current);
    onCommit?.(draftRef.current);
  }, [onCommit]);
  const scheduleKeyboardCommit = useCallback(() => {
    clearTimeout(keyboardTimer.current);
    keyboardTimer.current = setTimeout(commit, 300);
  }, [commit]);
  return <input data-testid={testId} aria-label={label} title={label} className={`${className}${vertical ? ' ba-range-vertical' : ''}`} type="range"
    min={min} max={max} step={step} value={draft}
    onPointerDown={(event) => { pointerRef.current = true; clearTimeout(keyboardTimer.current); event.currentTarget.setPointerCapture?.(event.pointerId); }}
    onChange={(event) => {
      const next = clamp(event.target.value, min, max);
      draftRef.current = next; setDraft(next); onDraft?.(next);
      if (!pointerRef.current) scheduleKeyboardCommit();
    }}
    onPointerUp={() => { if (!pointerRef.current) return; pointerRef.current = false; commit(); }}
    onPointerCancel={() => { if (!pointerRef.current) return; pointerRef.current = false; commit(); }} />;
}

export const panLabel = (pan) => Number(pan) === 0 ? 'C' : Number(pan) < 0 ? `L${Math.abs(Math.round(pan))}` : `R${Math.round(pan)}`;

export function PanKnob({ trackId, value = 0, onDraft, onCommit }) {
  const [draft, setDraft] = useState(value);
  const draftRef = useRef(value);
  const dragRef = useRef(null);
  const keyboardTimer = useRef(null);
  useEffect(() => { if (!dragRef.current) { draftRef.current = value; setDraft(value); } }, [value]);
  useEffect(() => () => clearTimeout(keyboardTimer.current), []);
  const preview = useCallback((next) => { const clean = clamp(next, -50, 50); draftRef.current = clean; setDraft(clean); onDraft?.(clean); }, [onDraft]);
  const commit = useCallback(() => { clearTimeout(keyboardTimer.current); onCommit?.(draftRef.current); }, [onCommit]);
  const begin = (event) => {
    event.preventDefault();
    dragRef.current = { y: event.clientY, value: draftRef.current };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const move = (event) => { if (dragRef.current) preview(dragRef.current.value + (dragRef.current.y - event.clientY) * 0.5); };
  const finish = () => { if (!dragRef.current) return; dragRef.current = null; commit(); };
  const keyDown = (event) => {
    if (!['ArrowUp', 'ArrowRight', 'ArrowDown', 'ArrowLeft', 'Home'].includes(event.key)) return;
    event.preventDefault();
    const direction = ['ArrowUp', 'ArrowRight'].includes(event.key) ? 1 : -1;
    preview(event.key === 'Home' ? 0 : draftRef.current + direction * (event.shiftKey ? 5 : 1));
    clearTimeout(keyboardTimer.current);
    keyboardTimer.current = setTimeout(commit, 300);
  };
  const rotation = -135 + ((draft + 50) / 100) * 270;
  return <div data-testid={`pan-${trackId}`} role="slider" tabIndex={0} aria-label="Pan" aria-valuemin={-50} aria-valuemax={50} aria-valuenow={draft}
    title={`Pan ${panLabel(draft)}`} className="ba-pan-knob" onPointerDown={begin} onPointerMove={move} onPointerUp={finish} onPointerCancel={finish}
    onDoubleClick={() => { preview(0); draftRef.current = 0; commit(); }} onKeyDown={keyDown}>
    <i style={{ transform: `rotate(${rotation}deg)` }} />
  </div>;
}
